import React, { useMemo, useState } from "react";
import { Code, ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";
import { getFeedbackForm } from "../../../../redux/reducer/incbotFeedbackReducer";

// --- Types ---

export interface SkillMetric {
  id: string;
  title: string;
  score: number;
  max: number;
  description: string;
}

export interface SkillSection {
  id: string;
  title: string;
  overallScore: number;
  overallMax: number;
  overallComment: string;
  metrics: SkillMetric[];
}

/** Shape of `feedback.psychometricSkillWise` from interview API */
export type PsychometricSkillWiseApi = {
  position?: string;
  skill_results?: Array<{
    skill: string;
    score: number;
    comment: string;
    sub_skills?: Array<{ sub_skill: string; score: number; comment: string }>;
  }>;
};

function roundPsychScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round(value);
}

function formatPsychScore(value: number): string {
  return String(roundPsychScore(value));
}

function slugifySkillId(name: string, index: number): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  return base || `skill-${index}`;
}

function mapPsychometricSkillWiseToSections(
  raw: PsychometricSkillWiseApi | null | undefined
): SkillSection[] | null {
  if (!raw?.skill_results?.length) return null;
  return raw.skill_results.map((row, idx): SkillSection => {
    const title = String(row.skill ?? `Skill ${idx + 1}`).trim();
    const subs = Array.isArray(row.sub_skills) ? row.sub_skills : [];
    const metrics: SkillMetric[] = subs.map((sub, j) => {
      const subRaw = Number(sub.score);
      return {
        id: `${slugifySkillId(title, idx)}-sub-${j}`,
        title: String(sub.sub_skill ?? `Sub-skill ${j + 1}`).trim(),
        score: roundPsychScore(subRaw),
        max: 10,
        description: String(sub.comment ?? "").trim() || "—",
      };
    });
    const overall = Number(row.score);
    return {
      id: slugifySkillId(title, idx),
      title,
      overallScore: roundPsychScore(overall),
      overallMax: 10,
      overallComment: String(row.comment ?? "").trim() || "—",
      metrics,
    };
  });
}

// --- Mock data (fallback when API has no psychometricSkillWise) ---

const MOCK_SKILLWISE_SECTIONS: SkillSection[] = [
  {
    id: "communication",
    title: "Communication Skills",
    overallScore: 8,
    overallMax: 10,
    overallComment:
      "The candidate demonstrates strong verbal clarity and adapts tone appropriately for the audience. Listening cues are consistent; continued focus on concise structuring in high-pressure scenarios will further strengthen executive presence.",
    metrics: [
      {
        id: "c1",
        title: "Active Listening",
        score: 8,
        max: 10,
        description: "Reflects key points accurately and asks relevant follow-up questions.",
      },
      {
        id: "c2",
        title: "Verbal Communication",
        score: 8,
        max: 10,
        description: "Articulates ideas clearly with minimal filler and good pacing.",
      },
      {
        id: "c3",
        title: "Presentation Skills",
        score: 7,
        max: 10,
        description: "Logical flow is solid; visual support and signposting could be tighter.",
      },
      {
        id: "c4",
        title: "Non-Verbal Communication",
        score: 8,
        max: 10,
        description: "Maintains appropriate eye contact and open posture throughout.",
      },
    ],
  },
  {
    id: "emotional-intelligence",
    title: "Emotional Intelligence",
    overallScore: 7,
    overallMax: 10,
    overallComment:
      "Shows good awareness of others’ perspectives and manages own reactions under mild stress. Deepening empathy in conflict scenarios and proactive relationship repair would elevate performance further.",
    metrics: [
      {
        id: "e1",
        title: "Empathy",
        score: 7,
        max: 10,
        description: "Recognizes emotional cues and validates concerns in most exchanges.",
      },
      {
        id: "e2",
        title: "Relationship Management",
        score: 7,
        max: 10,
        description: "Builds rapport steadily; could initiate more stakeholder touchpoints.",
      },
      {
        id: "e3",
        title: "Social Skills",
        score: 8,
        max: 10,
        description: "Collaborates comfortably in group settings and mediates minor friction.",
      },
      {
        id: "e4",
        title: "Self-Regulation",
        score: 7,
        max: 10,
        description: "Stays composed; occasional impatience surfaces when pace slows.",
      },
    ],
  },
  {
    id: "stress-resilience",
    title: "Stress & Resilience",
    overallScore: 8,
    overallMax: 10,
    overallComment:
      "Recovers quickly from setbacks and maintains task focus. Stress responses are generally constructive; documenting coping patterns would help sustain performance in peak-load periods.",
    metrics: [
      {
        id: "s1",
        title: "Adaptability",
        score: 8,
        max: 10,
        description: "Adjusts plans when priorities shift without losing momentum.",
      },
      {
        id: "s2",
        title: "Emotional Resilience",
        score: 8,
        max: 10,
        description: "Bounces back from criticism and reframes challenges positively.",
      },
      {
        id: "s3",
        title: "Coping Strategies",
        score: 7,
        max: 10,
        description: "Uses time-boxing and breaks; could diversify long-term strategies.",
      },
      {
        id: "s4",
        title: "Perseverance",
        score: 8,
        max: 10,
        description: "Follows through on difficult tasks until a workable outcome is reached.",
      },
    ],
  },
  {
    id: "teamwork",
    title: "Teamwork Skills",
    overallScore: 9,
    overallMax: 10,
    overallComment:
      "Highly dependable in team contexts: shares credit, clarifies ownership, and escalates blockers early. Minor opportunity to delegate more evenly to develop bench strength.",
    metrics: [
      {
        id: "t1",
        title: "Collaboration",
        score: 9,
        max: 10,
        description: "Integrates inputs from peers and aligns on shared goals quickly.",
      },
      {
        id: "t2",
        title: "Conflict Resolution",
        score: 8,
        max: 10,
        description: "Addresses disagreements with facts and seeks win-win outcomes.",
      },
      {
        id: "t3",
        title: "Reliability",
        score: 9,
        max: 10,
        description: "Meets commitments and communicates delays proactively.",
      },
      {
        id: "t4",
        title: "Cross-functional Collaboration",
        score: 9,
        max: 10,
        description: "Bridges disciplines effectively and respects domain constraints.",
      },
    ],
  },
  {
    id: "decision-making",
    title: "Decision-Making Skills",
    overallScore: 7,
    overallMax: 10,
    overallComment:
      "Balances speed and rigor in most decisions. Risk framing is sound; strengthening explicit trade-off documentation and stakeholder sign-off checkpoints would reduce downstream rework.",
    metrics: [
      {
        id: "d1",
        title: "Prioritization",
        score: 8,
        max: 10,
        description: "Ranks initiatives by impact and urgency with reasonable consistency.",
      },
      {
        id: "d2",
        title: "Risk Assessment",
        score: 7,
        max: 10,
        description: "Identifies main risks; secondary risks sometimes under-explored.",
      },
      {
        id: "d3",
        title: "Strategic Thinking",
        score: 7,
        max: 10,
        description: "Connects tactics to broader goals; horizon could extend further.",
      },
      {
        id: "d4",
        title: "Judgment",
        score: 7,
        max: 10,
        description: "Sound calls in routine cases; seeks validation on novel problems.",
      },
    ],
  },
  {
    id: "creativity",
    title: "Creativity",
    overallScore: 8,
    overallMax: 10,
    overallComment:
      "Generates useful alternatives and reframes problems constructively. Ideas are practical; pushing a few concepts further into experimentation would unlock more breakthrough options.",
    metrics: [
      {
        id: "cr1",
        title: "Innovation",
        score: 8,
        max: 10,
        description: "Proposes novel approaches that remain feasible within constraints.",
      },
      {
        id: "cr2",
        title: "Creative Problem Solving",
        score: 8,
        max: 10,
        description: "Combines existing tools in inventive ways to unblock issues.",
      },
      {
        id: "cr3",
        title: "Design Thinking",
        score: 7,
        max: 10,
        description: "User empathy is evident; prototype iteration cycles could be faster.",
      },
      {
        id: "cr4",
        title: "Originality",
        score: 8,
        max: 10,
        description: "Avoids cookie-cutter solutions; adds distinct perspective to discussions.",
      },
    ],
  },
  {
    id: "strategic-thinking",
    title: "Strategic Thinking",
    overallScore: 8,
    overallMax: 10,
    overallComment:
      "Demonstrates solid grasp of market signals and long-horizon implications. Connecting metrics to narrative for leadership audiences would sharpen strategic storytelling.",
    metrics: [
      {
        id: "st1",
        title: "Long-term Planning",
        score: 8,
        max: 10,
        description: "Outlines milestones and dependencies across multi-quarter horizons.",
      },
      {
        id: "st2",
        title: "Market Awareness",
        score: 8,
        max: 10,
        description: "Cites relevant trends and competitor moves with credible sources.",
      },
      {
        id: "st3",
        title: "Vision Setting",
        score: 7,
        max: 10,
        description: "Inspiring direction is clear; quantified success criteria help next.",
      },
      {
        id: "st4",
        title: "Competitive Analysis",
        score: 8,
        max: 10,
        description: "Differentiates positioning and identifies whitespace opportunities.",
      },
    ],
  },
  {
    id: "research",
    title: "Research Skills",
    overallScore: 7,
    overallMax: 10,
    overallComment:
      "Methodical in gathering and validating information. Analysis depth is good; synthesis into executive summaries and explicit source hierarchy would improve decision-ready outputs.",
    metrics: [
      {
        id: "r1",
        title: "Information Gathering",
        score: 8,
        max: 10,
        description: "Uses diverse channels and documents search strategy clearly.",
      },
      {
        id: "r2",
        title: "Analysis",
        score: 7,
        max: 10,
        description: "Breaks down complex data; edge cases occasionally underweighted.",
      },
      {
        id: "r3",
        title: "Synthesis",
        score: 7,
        max: 10,
        description: "Connects findings to recommendations; tighter executive summary needed.",
      },
      {
        id: "r4",
        title: "Source Verification",
        score: 8,
        max: 10,
        description: "Cross-checks claims and flags confidence levels appropriately.",
      },
    ],
  },
];

// --- Subcomponents ---

function SkillMetricCard({ metric }: { metric: SkillMetric }) {
  const scoreRounded = roundPsychScore(metric.score);
  const pct = metric.max > 0 ? Math.min(100, Math.round((scoreRounded / metric.max) * 100)) : 0;

  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 md:p-4 duration-200">
      <div className="mb-2 flex items-start justify-between gap-2">
        <span className="text-sm font-semibold leading-snug text-black">{metric.title}</span>
        <span className="shrink-0 text-sm font-semibold tabular-nums text-black">
          {formatPsychScore(scoreRounded)}
        </span>
      </div>
      <div className="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-[linear-gradient(135deg,_#1375FF_0%,_#133F7D_100%)] transition-[width] duration-300 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs leading-relaxed text-[#374151]">{metric.description}</p>
    </div>
  );
}

function SkillSectionCard({ section }: { section: SkillSection }) {
  const hasMetrics = section.metrics.length > 0;
  /** When false: header + overall comment only. When true: also show sub-skill grid. */
  const [metricsOpen, setMetricsOpen] = useState(true);
  const headingId = `psychometric-section-${section.id}-title`;

  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-4 duration-200 md:p-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 id={headingId} className="min-w-0 flex-1 font-bold leading-snug text-black md:text-lg">
          {section.title}
        </h3>
        <div className="flex shrink-0 items-center gap-1">
          <span className="rounded-full bg-[#F0FDF4] py-1 px-7 text-sm font-semibold tabular-nums text-[#15803D]">
            {formatPsychScore(section.overallScore)}/{section.overallMax}
          </span>
          {hasMetrics ? (
            <button
              type="button"
              onClick={() => setMetricsOpen((v) => !v)}
              className="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 outline-none transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500/40"
              aria-expanded={metricsOpen}
              aria-controls={`psychometric-section-${section.id}-metrics`}
              aria-labelledby={headingId}
              aria-label={metricsOpen ? "Hide skill breakdown" : "Show skill breakdown"}
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${metricsOpen ? "rotate-180" : ""}`}
                aria-hidden
              />
            </button>
          ) : null}
        </div>
      </div>

      <div className="border-t border-[#E5E7EB] pt-4 mt-3" aria-labelledby={headingId}>
        <p className="mb-1 text-sm font-bold text-black">Overall Comment:</p>
        <p
          className={`text-sm leading-relaxed text-[#374151] ${metricsOpen && hasMetrics ? "mb-5" : "mb-0"}`}
        >
          {section.overallComment}
        </p>
        {metricsOpen && hasMetrics ? (
          <div
            id={`psychometric-section-${section.id}-metrics`}
            role="region"
            aria-label="Sub-skill breakdown"
            className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4"
          >
            {section.metrics.map((metric) => (
              <SkillMetricCard key={metric.id} metric={metric} />
            ))}
          </div>
        ) : null}
        {!hasMetrics ? (
          <p className="text-xs text-gray-500 mt-2">No sub-skill breakdown for this category.</p>
        ) : null}
      </div>
    </div>
  );
}

// --- Page ---

const PsychometricAnalysisView: React.FC = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const apiWise = feedbackForm?.psychometricSkillWise as
    | PsychometricSkillWiseApi
    | null
    | undefined;

  const sections = useMemo(() => {
    const fromApi = mapPsychometricSkillWiseToSections(apiWise);
    return fromApi && fromApi.length > 0 ? fromApi : MOCK_SKILLWISE_SECTIONS;
  }, [apiWise]);

  const usingApi = Boolean(apiWise?.skill_results && apiWise.skill_results.length > 0);
  const positionLabel = usingApi ? apiWise?.position?.trim() : "";

  return (
    <div className="bg-[#f5f6f8] p-3 md:p-6">
      <div className="md:hidden print:flex mb-3 w-full rounded-md border-b-4 border-teal-500 bg-teal-50 p-1 text-center text-sm font-semibold text-teal-600 print:mb-6 print:flex print:p-2 print:text-xl">
        Psychometric Analysis
      </div>

      <div className="rounded-xl border border-[#E5E7EB] bg-white shadow-sm">
        <div className="border-b border-[#E5E7EB] px-4 py-4 md:px-6 md:py-5">
          <div className="flex items-start gap-3">
            <span
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700 shadow-sm ring-1 ring-sky-200/60"
              aria-hidden
            >
              <Code className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold tracking-tight text-black md:text-xl">
                Skill wise Score
              </h2>
              {positionLabel ? (
                <p className="mt-1 text-sm text-gray-600">Target role: {positionLabel}</p>
              ) : null}
              {usingApi ? (
                <p className="mt-1 text-xs text-gray-500">
                  Based on interview responses and psychometric scoring.
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 items-start gap-4 md:gap-6">
            {sections.map((section) => (
              <SkillSectionCard key={section.id} section={section} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PsychometricAnalysisView;
